import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Hammer, Car, Cpu, Shirt, Home, Wrench, Heart, Users, Eye, MapPin, ArrowLeft, Send, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  automobile:   { label: "Automobile Engineering", Icon: Car,    color: "#FF2D7A" },
  crafts:       { label: "Crafts & Artisan",       Icon: Hammer, color: "#FFB42D" },
  electronics:  { label: "Electronics & Robotics", Icon: Cpu,    color: "#00E0C7" },
  fashion:      { label: "Fashion Design",         Icon: Shirt,  color: "#A45DFF" },
  architecture: { label: "Architecture & Furniture", Icon: Home, color: "#FF6B00" },
  other:        { label: "Other Builds",           Icon: Wrench, color: "#737B7E" },
};

const SponsorModal = ({ open, onClose, showcase, user, onSent }) => {
  const [form, setForm] = useState({
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    company: "",
    pledge_amount: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const send = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Add a quick note (10+ characters)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        company: form.company.trim() || null,
        pledge_amount: form.pledge_amount ? Number(form.pledge_amount) : null,
        message: form.message.trim(),
      };
      await api.post(`/makers/showcases/${showcase.showcase_id}/interest`, payload);
      toast.success("Interest sent — the maker will be in touch.");
      onSent?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send — please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="makers-sponsor-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Express interest</div>
            <div className="font-display text-lg leading-tight mt-0.5">{showcase.title}</div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 rounded-full hover:bg-stone-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={send} className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input
              required
              type="text"
              placeholder="Your name"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              data-testid="makers-sponsor-name"
              className="rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-stone-900"
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              data-testid="makers-sponsor-email"
              className="rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-stone-900"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Company (optional)"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="makers-sponsor-company"
              className="rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-stone-900"
            />
            <input
              type="number"
              min="0"
              placeholder="Pledge amount £ (optional)"
              value={form.pledge_amount}
              onChange={(e) => setForm({ ...form, pledge_amount: e.target.value })}
              data-testid="makers-sponsor-pledge"
              className="rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-stone-900"
            />
          </div>
          <textarea
            required
            rows={4}
            minLength={10}
            placeholder="Hi, I'd love to learn more about your work and discuss how we could collaborate or sponsor…"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="makers-sponsor-message"
            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-stone-900 resize-y"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="makers-sponsor-submit"
            className="w-full py-3 rounded-full font-bold text-[#0A0A0A] disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            {submitting ? "Sending…" : (
              <span className="inline-flex items-center justify-center gap-2">
                Send interest <Send className="w-4 h-4" />
              </span>
            )}
          </button>
          <p className="text-[11px] text-stone-500 text-center">
            We pass your message + contact straight to the maker. No middlemen.
          </p>
        </form>
      </div>
    </div>
  );
};

const MakerDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/makers/showcases/${id}`)
      .then((r) => setShowcase(r.data))
      .catch(() => toast.error("Showcase not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }
  if (!showcase) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-5">
        <div className="text-center">
          <Hammer className="w-12 h-12 mx-auto text-stone-300 mb-3" />
          <div className="font-semibold">Showcase not found</div>
          <Link to="/makers" className="text-sm text-stone-500 underline mt-2 inline-block">
            Back to all showcases
          </Link>
        </div>
      </div>
    );
  }

  const meta = CATEGORY_META[showcase.category] || CATEGORY_META.other;
  const { Icon } = meta;
  const images = showcase.image_urls || [];
  const hero = images[activeImg];

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="makers-detail-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <Link to="/makers" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to exhibition
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="relative aspect-square sm:aspect-[4/3] rounded-2xl overflow-hidden bg-stone-200 border border-stone-200">
              {hero ? (
                <img src={hero} alt={showcase.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300">
                  <Icon className="w-20 h-20" />
                </div>
              )}
              <div
                className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                style={{ background: meta.color }}
              >
                <Icon className="w-3 h-3" /> {meta.label}
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {images.map((u, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`makers-thumb-${i}`}
                    className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                      i === activeImg ? "border-stone-900" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="makers-detail-title">
              {showcase.title}
            </h1>
            <div className="flex items-center gap-3 mt-3 text-sm text-stone-600">
              <span className="font-semibold">{showcase.creator_name}</span>
              <span className="text-stone-400">·</span>
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {showcase.country}{showcase.region ? `, ${showcase.region}` : ""}
              </span>
            </div>

            <div className="mt-5 flex items-center gap-5 text-sm text-stone-600">
              <span className="inline-flex items-center gap-1.5">
                <Users className="w-4 h-4" /> {showcase.sponsor_count} interested
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Eye className="w-4 h-4" /> {showcase.view_count} views
              </span>
            </div>

            {showcase.funding_goal > 0 && (
              <div className="mt-5 p-3 rounded-xl bg-white border border-stone-200">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Funding goal</div>
                <div className="font-display text-2xl mt-1">£{showcase.funding_goal.toLocaleString()}</div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setSponsorOpen(true)}
              data-testid="makers-sponsor-cta"
              className="mt-6 w-full py-3.5 rounded-full font-bold text-[#0A0A0A]"
              style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Heart className="w-4 h-4" /> Sponsor or invest in this maker
              </span>
            </button>

            <p className="mt-3 text-[11px] text-stone-500 text-center">
              Your interest goes straight to the maker. AfriMarket never touches your money.
            </p>
          </div>
        </div>

        {/* Story */}
        <div className="mt-10 max-w-3xl">
          <h2 className="font-display text-2xl mb-3">The story</h2>
          <div className="prose prose-stone max-w-none whitespace-pre-wrap text-stone-700 leading-relaxed" data-testid="makers-detail-story">
            {showcase.story}
          </div>
          {showcase.video_url && (
            <div className="mt-6">
              <a
                href={showcase.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-bold"
              >
                Watch video →
              </a>
            </div>
          )}
        </div>
      </div>

      <SponsorModal
        open={sponsorOpen}
        onClose={() => setSponsorOpen(false)}
        showcase={showcase}
        user={user}
        onSent={() => setShowcase((s) => ({ ...s, sponsor_count: (s.sponsor_count || 0) + 1 }))}
      />
    </div>
  );
};

export default MakerDetail;
